
/**
 * Hook para exportação do dashboard em PDF
 * 
 * O que faz:
 * - Gerencia os estados do modal de exportação
 * - Captura elementos DOM usando html2canvas
 * - Gera PDF usando jsPDF com formatos A4 e Mobile (1080x1920)
 * - Faz download automático do arquivo
 * 
 * Onde é usado:
 * - Componente Dashboard para exportação PDF
 * 
 * Como se conecta:
 * - Usa html2canvas para capturar elementos visuais
 * - Usa jsPDF para gerar o arquivo PDF
 * - Integra com dados do dashboard existente
 */

import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { renderDashboardPdf } from '@/features/dashboard/report/dashboardPdfRenderer';
import type { DashboardReportFormat } from '@/features/dashboard/report/dashboardReportFormats';

export type ExportFormat = DashboardReportFormat;

interface ExportOptions {
  format: ExportFormat;
  clinicaName?: string;
  periodo?: string;
  generatedAt: Date;
  sourceElement: HTMLElement;
}

export const useDashboardExport = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('a4');
  const [isExporting, setIsExporting] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<Date | null>(null);
  const { toast } = useToast();

  // Abre o modal de exportação
  const openExportModal = () => {
    // Congela o horário ao abrir para preview, nome do arquivo e PDF exibirem a mesma referência temporal.
    setGeneratedAt(new Date());
    setIsModalOpen(true);
  };

  // Fecha o modal de exportação
  const closeExportModal = () => {
    setIsModalOpen(false);
    setSelectedFormat('a4');
    setGeneratedAt(null);
  };

  // Altera o formato selecionado
  const changeFormat = (format: ExportFormat) => {
    setSelectedFormat(format);
  };

  // Função principal para exportar o dashboard
  const exportDashboard = async (options: ExportOptions) => {
    setIsExporting(true);
    
    try {
      const hoje = format(options.generatedAt, 'dd-MM-yyyy', { locale: ptBR });
      const formatoTexto = options.format === 'a4' ? 'A4' : 'Mobile';
      const fileName = `Dashboard_${options.clinicaName || 'Clinica'}_${options.periodo || 'Periodo'}_${formatoTexto}_${hoje}.pdf`;

      await renderDashboardPdf({ sourceElement: options.sourceElement, format: options.format, fileName });

      toast({
        title: "PDF gerado com sucesso!",
        description: `Dashboard exportado em formato ${formatoTexto}`,
      });

      // Fechar modal após sucesso
      closeExportModal();

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast({
        title: "Erro na exportação",
        description: "Não foi possível gerar o PDF. O relatório continua aberto para você tentar novamente.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    isModalOpen,
    selectedFormat,
    isExporting,
    generatedAt,
    openExportModal,
    closeExportModal,
    changeFormat,
    exportDashboard,
  };
};
